import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import { VFC } from "react";
import styled from "styled-components";
import { LegacyImage, Link, Markdown } from "components";
import { useAnchor } from "hooks";
import { FullArrow } from "icons";
import { Features34Content } from "types/graphql/generated";

interface Props {
  content: Features34Content;
}

const Features34: VFC<Props> = withErrorBoundary(
  ({ content: { features34Title, features34Links, features34Disclaimer } }) => {
    useAnchor("features34");

    return (
      <StyledFeatures34 className="features34">
        <div className="Content">
          <Markdown className="Font_48_54_600">{features34Title}</Markdown>
          <ul className="Features34__linksList">
            {features34Links.map(
              ({
                id,
                features34Link,
                features34LinkLabel,
                logo: {
                  data: { logo },
                },
              }) => (
                <li key={id} className="Features34__linkContainer">
                  <Link href={features34Link}>
                    <LegacyImage {...logo} transform={{ format: "svg" }} className="Features32__logo" />
                  </Link>
                  <Link href={features34Link} className="Features34__link Font_24_32_600">
                    {features34LinkLabel}
                    <FullArrow className="Features43__arrow" />
                  </Link>
                </li>
              )
            )}
          </ul>
          <Markdown className="Features34__disclaimer Color_tertiary Font_12_16_450">{features34Disclaimer}</Markdown>
        </div>
      </StyledFeatures34>
    );
  },
  {}
);

const StyledFeatures34 = styled.section`
  background: #faf7f4;

  .Features34__linksList {
    margin-top: 64px;
    list-style: none;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    grid-gap: 32px;
  }

  .Features34__linkContainer {
    background: ${({ theme }) => theme.colors.fog["00"]};
    padding: 40px 32px;
    border-radius: 8px;
    box-shadow:
      0 1px 2px rgb(25 24 23 / 16%),
      0 2px 4px rgb(25 24 23 / 4%);
  }

  .Features32__logo {
    height: 40px;
  }

  .Features34__link {
    padding-top: 24px;
    margin-top: 24px;
    width: 100%;
    border-top: 1px solid ${({ theme }) => theme.colors.fog["20"]};
    display: flex;
    justify-content: space-between;
    align-items: center;

    &:hover {
      opacity: 0.7;
    }
  }

  .Features43__arrow {
    fill: ${({ theme }) => theme.colors.fog["80"]};
    width: 24px;
    height: 24px;
  }

  .Features34__disclaimer {
    margin-top: 64px;
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.xl}px`}) {
    .Features34__linksList {
      grid-template-columns: 1fr 1fr;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.md}px`}) {
    .Features34__linksList {
      grid-template-columns: 1fr;
    }
  }
`;

gql`
  fragment Features34Content on ComponentFeaturesFeatures34 {
    features34Title
    features34Links {
      id
      features34LinkLabel
      features34Link
      logo {
        data {
          logo: attributes {
            src: hash
            alt: alternativeText
            width
            height
          }
        }
      }
    }
    features34Disclaimer
  }
`;

export { Features34 };
export type { Props as Features34Props };
